import type { BulkOperation } from "src/api/bulk-operations/type";
import type { BulkActionFeedbackStatus } from "src/components/MarketingActionFeedbackBanner/types";
import { OffersTablesTabsEnum, offersRoutes } from "src/views/offer/routes";
import { SponsoredAdsTablesTabsEnum, sponsoredAdsRoutes } from "src/views/sponsored-ads/routes";

type TFunc = (key: string, options?: Record<string, unknown>) => string;

export function hasSpecificIds(value: unknown): boolean {
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
}

export function getFeedbackStatus(operation: BulkOperation): BulkActionFeedbackStatus {
    if (operation.status === "pending" || operation.status === "inProgress") {
        return "processing";
    }

    if (operation.status === "failed") {
        if (operation.erroredItems.length === operation.totalItems) {
            return "error";
        }
        return "partial";
    }

    return "success";
}

export function getProgressPercent(operation: BulkOperation): number {
    if (!operation.totalItems) {
        return 0;
    }
    return Math.round((operation.processedItems / operation.totalItems) * 100);
}

export function getEntityLabel(
    target: BulkOperation["target"],
    options: { plural?: boolean },
    tCommon: TFunc
): string {
    const isPlural = options?.plural ?? false;

    if (target === "offer") {
        return tCommon(isPlural ? "concepts.offers" : "concepts.offer");
    }

    return tCommon(isPlural ? "concepts.sponsoredAds" : "concepts.sponsoredAd");
}

export function getSubtitleText(
    operation: BulkOperation,
    t: TFunc,
    tCommon: TFunc
): string[] | null {
    const status = getFeedbackStatus(operation);
    const failedCount = operation.erroredItems.length;
    const totalCount = operation.totalItems;
    const succeededCount = operation.processedItems - failedCount;

    switch (status) {
        case "processing":
            return [
                t("status.processing", {
                    count: totalCount,
                }),
            ];
        case "success":
            return [
                t("status.success", {
                    count: totalCount,
                    context: operation.target,
                    entity: getEntityLabel(operation.target, { plural: totalCount > 1 }, tCommon),
                    action: t(`operation-type.${operation.operationType}`, {
                        count: totalCount,
                        context: operation.target,
                    }),
                }),
            ];
        case "partial":
            return [
                t("status.partial-success", {
                    succeeded: succeededCount,
                    context: operation.target,
                    entity: getEntityLabel(
                        operation.target,
                        { plural: succeededCount > 1 },
                        tCommon
                    ),
                    action: t(`operation-type.${operation.operationType}`, {
                        count: succeededCount,
                        context: operation.target,
                    }),
                    count: succeededCount,
                }),
                t("status.partial-failed", {
                    failed: failedCount ?? 0,
                    context: operation.target,
                    entity: getEntityLabel(operation.target, { plural: failedCount > 1 }, tCommon),
                    action: t(`operation-type.${operation.operationType}`, {
                        count: failedCount,
                        context: operation.target,
                    }),
                    count: failedCount,
                }),
            ];
        case "error":
            return [
                t("status.error", {
                    context: operation.target,
                    entity: getEntityLabel(operation.target, { plural: failedCount > 1 }, tCommon),
                    action: t(`operation-type.${operation.operationType}`, {
                        count: failedCount,
                        context: operation.target,
                    }),
                }),
            ];
        default:
            return null;
    }
}

export function getErrorButtonLabel(
    operation: BulkOperation,
    status: BulkActionFeedbackStatus,
    t: TFunc,
    tCommon: TFunc
): string | null {
    if (!["error", "partial"].includes(status)) {
        return null;
    }

    if (operation.operationType === "restart") {
        return t("error-button.view-errors");
    }

    if (operation.operationType === "edit" || operation.operationType === "cancel") {
        const count = operation.erroredItems.length;
        const entityLabel = getEntityLabel(operation.target, { plural: count > 1 }, tCommon);
        return t("error-button.see-affected", {
            count,
            context: operation.target,
            entity: entityLabel,
        });
    }

    return null;
}

export function getErrorNavigationUrl(operation: BulkOperation): string {
    const erroredIds = operation.erroredItems.map((item) => item.id);

    if (operation.operationType === "restart") {
        //TODO in this case we should ideally navigate to a dedicated error page showing the error details, but for now we'll just navigate to the tables view filtered by the errored items
        if (operation.target === "offer") {
            return offersRoutes.tablesView({
                tab: OffersTablesTabsEnum.MANAGEMENT,
                offerIds: erroredIds,
                fromMarketingFeedback: true,
            });
        }
        return sponsoredAdsRoutes.tablesView({
            tab: SponsoredAdsTablesTabsEnum.MANAGEMENT,
            sponsoredAdIds: erroredIds,
            fromMarketingFeedback: true,
        });
    }

    if (operation.target === "offer") {
        return offersRoutes.tablesView({
            tab: OffersTablesTabsEnum.MANAGEMENT,
            offerIds: erroredIds,
            fromMarketingFeedback: true,
        });
    }

    return sponsoredAdsRoutes.tablesView({
        tab: SponsoredAdsTablesTabsEnum.MANAGEMENT,
        sponsoredAdIds: erroredIds,
        fromMarketingFeedback: true,
    });
}
